import React, { useState } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { SearchBox } from 'office-ui-fabric-react/lib/SearchBox';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { useHistory } from 'react-router-dom';
import { useId } from '@uifabric/react-hooks';
import AutosuggestDropdown from '../AutosuggestDropdown';
import AutosuggestDropdownItem from '../AutosuggestDropdownItem';
import styles from './SearchBar.style';

const debouncedOnChange = debounce((_value, onChange) => {
  if (onChange) onChange(_value);
}, 400);

const SearchBar = ({
  onChange,
  onSearch,
  onClick,
  onClear,
  placeholder,
  suggestions,
  isLoading,
  noResultsText,
  onRenderDropdown,
  onRenderSuggestion,
  debounceDelay,
  viewMore,
  className: classNameProp,
  value,
  underlined,
  accentError,
  hasValue,
  isVisible,
  ...others
}) => {
  const [input, setInput] = useState('');
  const history = useHistory();
  const searchBoxId = useId('searchBox');
  const className = clsx(styles.root, classNameProp);

  let searchBoxClassName = styles.searchBox;
  if (accentError) {
    searchBoxClassName += ` ${styles.accentError}`;
  }

  const handleClear = () => {
    setInput('');
    if (onClear) {
      onClear();
    }
  };

  const handleChange = e => {
    if (e) {
      e.persist();
      if (!hasValue) {
        setInput(e.target.value);
      }
      debouncedOnChange(e.target.value, onChange);
    }
  };

  return (
    <>
      {isVisible && (
        <Stack className={className}>
          <SearchBox
            id={searchBoxId}
            placeholder={placeholder}
            ariaLabel="Search"
            underlined={underlined}
            autoComplete="off"
            onSearch={onSearch}
            onChange={handleChange}
            data-testid="search-input"
            onClear={handleClear}
            className={searchBoxClassName}
            // In ContracttypeAhead, value is passed on selection, which needs to be displayed. For other cases display the user input
            value={value || input}
            {...others}
          />
          {/* Show the suggestions */}
          {input &&
            suggestions &&
            onRenderDropdown({
              target: searchBoxId,
              suggestions,
              isLoading,
              noResultsText,
              onRenderSuggestion,
              viewMore,
              onClick,
              history,
            })}
        </Stack>
      )}
    </>
  );
};

SearchBar.propTypes = {
  placeholder: PropTypes.string,
  /**
   * Callback that is fired whenever a user types. Whenever the value of the search
   * input field changes, this is triggered. It's always debounced, and you can change
   * the debounce delay by updating the `debounceDelay` prop.
   */
  onChange: PropTypes.func,
  /**
   * Callback that is fired when a user clicks on a suggestion. If this isn't set but the suggestion
   * object has a `href` property, clicking the suggestion will act like a link to that href.
   */
  onClick: PropTypes.func,
  /**
   * Callback that is fired when a user clicks the X that appears in the right of the text field.
   */
  onClear: PropTypes.func,
  /**
   * Callback that is fired when a user presses enter. Is passed the current search input value.
   */
  onSearch: PropTypes.func,
  /**
   * Number of milliseconds the search bar will wait between calls of `onChange`.
   */
  debounceDelay: PropTypes.number,
  /**
   * The suggestions you'd like to show in the search results suggestion dropdown. If nothing
   * is passed for these, then the dropdown will never appear.
   */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      value: PropTypes.node.isRequired,
      label: PropTypes.node.isRequired,
      secondaryLabel: PropTypes.node,
      tertiaryLabel: PropTypes.node,
      href: PropTypes.string,
    }),
  ),
  /**
   * Whether suggestions are loading or not. Doesn't really need to be passed unless
   * you want to hide old results while loading new ones.
   */
  isLoading: PropTypes.bool,
  /**
   * Custom component allowing you to render a custom autosuggest dropdown.
   */
  onRenderDropdown: PropTypes.func,
  /**
   * Custom component allowing you to render custom autosuggest items in the dropdown.
   */
  onRenderSuggestion: PropTypes.func,
  /**
   * Details of the view more link at the bottom of the autosuggest results
   */
  viewMore: PropTypes.shape({
    href: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
  className: PropTypes.string,
  noResultsText: PropTypes.string,
  value: PropTypes.string,
  underlined: PropTypes.bool,
  accentError: PropTypes.bool,
  hasValue: PropTypes.bool,
  isVisible: PropTypes.bool,
};

SearchBar.defaultProps = {
  isVisible: true,
  placeholder: 'Search',
  onSearch: null,
  onChange: null,
  onClick: null,
  onClear: null,
  suggestions: null,
  isLoading: null,
  onRenderDropdown: AutosuggestDropdown,
  onRenderSuggestion: AutosuggestDropdownItem,
  debounceDelay: 400,
  viewMore: null,
  className: '',
  noResultsText: undefined,
  value: '',
  underlined: true,
  accentError: false,
  hasValue: false,
};

export default SearchBar;
